import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "payment-block" }
const _hoisted_2 = { class: "payment-header" }
const _hoisted_3 = { class: "payment-header-left" }
const _hoisted_4 = {
  key: 0,
  class: "payment-amount-value payment-header-amount text-success"
}
const _hoisted_5 = {
  key: 1,
  class: "payment-missing"
}
const _hoisted_6 = {
  key: 2,
  class: "payment-error"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  loadStripe,
  type Stripe,
  type StripeCardElement,
  type StripeElements,
  type StripeCardElementChangeEvent,
  type PaymentIntentConfirmParams,
  type PaymentIntentResult,
  type SetupIntentResult,
  type StripeCardElementOptions,
  type StripeConstructorOptions,
} from "@stripe/stripe-js";

export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentBlock',
  props: {
    publishableKey: { default: "" },
    stripeAccount: { default: "" },
    amount: { default: null },
    currency: { default: "USD" }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const defaultPublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || "";

const resolvedPublishableKey = computed(() => props.publishableKey || defaultPublishableKey);
const resolvedStripeAccount = computed(() => props.stripeAccount || "");

const showAmount = computed(() => typeof props.amount === "number" && Number.isFinite(props.amount));
const formattedAmount = computed(() => {
  const amount = props.amount;
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: props.currency || "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if currency code is invalid.
    return `$${amount.toFixed(2)}`;
  }
});

const stripeCardMount = ref<HTMLElement | null>(null);
const stripeError = ref("");
const stripeInstance = ref<Stripe | null>(null);
const stripeElements = ref<StripeElements | null>(null);
const stripeCardElement = ref<StripeCardElement | null>(null);
const stripeReady = ref(false);
const stripeMounted = ref(false);
const stripeElementReady = ref(false);
const cardComplete = ref(false);
const stripeInitPromise = ref<Promise<void> | null>(null);
const stripeMountPromise = ref<Promise<void> | null>(null);
const mountedStripeHost = ref<HTMLElement | null>(null);

function resetStripeState() {
  stripeMounted.value = false;
  stripeElementReady.value = false;
  stripeReady.value = false;
  stripeInitPromise.value = null;
  stripeMountPromise.value = null;
  stripeElements.value = null;
  stripeInstance.value = null;
  mountedStripeHost.value = null;
}

function destroyStripeCard() {
  if (stripeCardElement.value && stripeMounted.value) {
    stripeCardElement.value.unmount();
  }

  if (stripeCardElement.value) {
    stripeCardElement.value.destroy();
    stripeCardElement.value = null;
  }

  resetStripeState();
}

async function initStripeCard() {
  if (!resolvedPublishableKey.value || stripeReady.value) return;
  if (stripeInitPromise.value) {
    await stripeInitPromise.value;
    return;
  }

  stripeInitPromise.value = (async () => {
    stripeError.value = "";
    try {
      const loadOptions: StripeConstructorOptions | undefined = resolvedStripeAccount.value
        ? { stripeAccount: resolvedStripeAccount.value }
        : undefined;
      stripeInstance.value = await loadStripe(resolvedPublishableKey.value, loadOptions);
      if (!stripeInstance.value) {
        stripeError.value = "Unable to initialize Stripe.";
        return;
      }

      stripeElements.value = stripeInstance.value.elements();
      const cardOptions: StripeCardElementOptions = {
        hidePostalCode: true,
      };
      stripeCardElement.value = stripeElements.value.create("card", cardOptions);
      stripeCardElement.value.on("change", (event: StripeCardElementChangeEvent) => {
        stripeError.value = event.error?.message || "";
        cardComplete.value = Boolean(event.complete);
      });
      stripeCardElement.value.on("ready", () => {
        stripeElementReady.value = true;
      });
      stripeReady.value = true;
    } catch (error) {
      stripeError.value = error?.message || "Payment error";
    } finally {
      stripeInitPromise.value = null;
    }
  })();

  await stripeInitPromise.value;
}

async function mountStripeCard() {
  if (!resolvedPublishableKey.value) return;
  if (stripeMountPromise.value) {
    await stripeMountPromise.value;
    return;
  }

  stripeMountPromise.value = (async () => {
    await nextTick();
    if (!stripeCardMount.value?.isConnected) return;

    await initStripeCard();

    if (!stripeCardMount.value?.isConnected || !stripeCardElement.value) return;
    if (
      stripeMounted.value &&
      mountedStripeHost.value === stripeCardMount.value
    ) {
      return;
    }

    if (stripeMounted.value) {
      stripeCardElement.value.unmount();
      stripeMounted.value = false;
    }

    stripeElementReady.value = false;
    stripeCardElement.value.mount(stripeCardMount.value);
    stripeMounted.value = true;
    mountedStripeHost.value = stripeCardMount.value;
  })();

  try {
    await stripeMountPromise.value;
  } finally {
    stripeMountPromise.value = null;
  }
}

async function waitForStripeElementReady(timeoutMs = 3000) {
  if (stripeElementReady.value) return;

  const startedAt = Date.now();

  while (!stripeElementReady.value && Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

watch(
  () => ({ key: resolvedPublishableKey.value, account: resolvedStripeAccount.value }),
  async (nextConfig, previousConfig) => {
    if (
      previousConfig &&
      (previousConfig.key !== nextConfig.key ||
        previousConfig.account !== nextConfig.account)
    ) {
      destroyStripeCard();
    }

    if (!nextConfig.key) {
      destroyStripeCard();
      return;
    }

    if (!stripeCardMount.value?.isConnected) return;
    await mountStripeCard();
  },
  { immediate: false },
);

onMounted(async () => {
  await mountStripeCard();
});

onBeforeUnmount(() => {
  destroyStripeCard();
});

async function ensureReady() {
  await mountStripeCard();
  await waitForStripeElementReady();
  if (!stripeInstance.value || !stripeCardElement.value || !stripeElementReady.value) {
    stripeError.value = "Payment card is not ready yet. Please try again.";
    throw new Error(stripeError.value);
  }
}

async function validate() {
  stripeError.value = "";
  await ensureReady();
  if (!cardComplete.value) {
    stripeError.value = "Please enter your card details.";
    throw new Error(stripeError.value);
  }
}

async function confirmPayment(clientSecret: string, billingDetails: BillingDetails = {}) {
  stripeError.value = "";
  await ensureReady();

  const confirmParams = {
    payment_method: {
      card: stripeCardElement.value!,
      billing_details: billingDetails,
    },
  } as any;

  const result: PaymentIntentResult = await stripeInstance.value!.confirmCardPayment(
    clientSecret,
    confirmParams,
  );

  if (result.error) {
    stripeError.value = result.error.message || "Payment error";
    throw new Error(stripeError.value);
  }

  const pi = result.paymentIntent;
  return {
    paymentIntentId: pi?.id,
    paymentMethodId: pi?.payment_method as string | undefined,
    status: pi?.status,
    brand:
      (pi as any)?.charges?.data?.[0]?.payment_method_details?.card?.brand ||
      (pi as any)?.payment_method_options?.card?.network,
    last4: (pi as any)?.charges?.data?.[0]?.payment_method_details?.card?.last4,
  };
}

async function confirmSetup(clientSecret: string, billingDetails: BillingDetails = {}) {
  stripeError.value = "";
  await ensureReady();

  const result: SetupIntentResult = await stripeInstance.value!.confirmCardSetup(
    clientSecret,
    {
      payment_method: {
        card: stripeCardElement.value!,
        billing_details: billingDetails,
      },
    },
  );

  if (result.error) {
    stripeError.value = result.error.message || "Payment error";
    throw new Error(stripeError.value);
  }

  const si = result.setupIntent;
  return {
    setupIntentId: si?.id,
    paymentMethodId: si?.payment_method as string | undefined,
    status: si?.status,
  };
}

__expose({ confirmPayment, confirmSetup, validate });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_v_icon, null, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode("mdi-credit-card-outline", -1)
          ]))]),
          _: 1
        }),
        _cache[1] || (_cache[1] = _createElementVNode("span", null, "Payment", -1))
      ]),
      (showAmount.value)
        ? (_openBlock(), _createElementBlock("strong", _hoisted_4, _toDisplayString(formattedAmount.value), 1))
        : _createCommentVNode("", true)
    ]),
    _cache[3] || (_cache[3] = _createElementVNode("p", { class: "payment-help" }, " Enter your card details to secure the booking. ", -1)),
    (resolvedPublishableKey.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "stripeCardMount",
          ref: stripeCardMount,
          class: "stripe-card-mount"
        }, null, 512))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[2] || (_cache[2] = [
          _createTextVNode(" Stripe publishable key is missing. Set ", -1),
          _createElementVNode("strong", null, "VUE_APP_STRIPE_PUBLISHABLE_KEY", -1),
          _createTextVNode(". ", -1)
        ]))])),
    (stripeError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(stripeError.value), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})